import { useEffect, useRef } from "react";

export function MarketStrip({ children }) {
  // Gesture-полоска для Android Telegram WebView:
  // не меняем логику и CSS `touch-action` без проверки, иначе снова начнут "ломаться" вертикальные скроллы.
  const rootRef = useRef(null);
  const innerRef = useRef(null);
  const draggedRef = useRef(false);
  const pointerStateRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    startTranslate: 0,
    currentTranslate: 0,
    axis: null,
  });

  useEffect(() => {
    const root = rootRef.current;
    const inner = innerRef.current;
    if (!root || !inner) return undefined;
    const THRESHOLD = 8;

    const getMaxTranslate = () => {
      return Math.min(0, root.clientWidth - inner.scrollWidth);
    };

    const clamp = (value, min, max) => {
      return Math.max(min, Math.min(max, value));
    };

    const applyTranslate = (value) => {
      inner.style.transform = `translate3d(${value}px, 0, 0)`;
    };

    const onPointerDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const state = pointerStateRef.current;
      state.pointerId = e.pointerId;
      draggedRef.current = false;
      state.startX = e.clientX;
      state.startY = e.clientY;
      state.startTranslate = state.currentTranslate;
      state.axis = null;
      if (typeof root.setPointerCapture === "function") {
        try {
          root.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
    };

    const onPointerMove = (e) => {
      const state = pointerStateRef.current;
      if (state.pointerId !== e.pointerId) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (state.axis === null) {
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
        state.axis = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }

      if (state.axis === "v") {
        return;
      }

      if (e.cancelable) e.preventDefault();
      const next = clamp(state.startTranslate + dx, getMaxTranslate(), 0);
      state.currentTranslate = next;
      applyTranslate(next);
      if (Math.abs(dx) > 4) {
        draggedRef.current = true;
      }
    };

    const onPointerUp = (e) => {
      const state = pointerStateRef.current;
      if (state.pointerId !== e.pointerId) return;
      if (typeof root.releasePointerCapture === "function") {
        try {
          root.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      state.pointerId = null;
      state.axis = null;
      window.setTimeout(() => {
        draggedRef.current = false;
      }, 120);
    };

    const onResize = () => {
      const state = pointerStateRef.current;
      state.currentTranslate = clamp(state.currentTranslate, getMaxTranslate(), 0);
      applyTranslate(state.currentTranslate);
    };

    let resizeObserver = null;

    root.addEventListener("pointerdown", onPointerDown, { passive: true });
    root.addEventListener("pointermove", onPointerMove, { passive: false });
    root.addEventListener("pointerup", onPointerUp, { passive: true });
    root.addEventListener("pointercancel", onPointerUp, { passive: true });
    window.addEventListener("resize", onResize);
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(root);
      resizeObserver.observe(inner);
    }
    onResize();

    return () => {
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", onPointerUp);
      root.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("resize", onResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="marketStrip"
      onClickCapture={(e) => {
        if (!draggedRef.current) return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div ref={innerRef} className="marketStrip__inner">
        {children}
      </div>
    </div>
  );
}
